import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
    eachDayOfInterval,
    format,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    addMonths,
    subMonths,
    isSameMonth,
    isSameDay
} from 'date-fns';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export function MemoHeatmap({
    onDateSelect,
    selectedDate,
    initialMonth,
    className
}: {
    onDateSelect?: (date: string | undefined) => void,
    selectedDate?: string,
    initialMonth?: string,
    className?: string
}) {
    const { data: statsData, isLoading } = useQuery({
        queryKey: ['memo-stats'],
        queryFn: api.getMemoStats,
        placeholderData: (previousData) => previousData
    });

    const [currentMonth, setCurrentMonth] = useState(() => {
        if (initialMonth) {
            try {
                return new Date(initialMonth);
            } catch {
                return new Date();
            }
        }
        return new Date();
    });

    // Generate days for the current month view (including padding days)
    const days = useMemo(() => {
        const start = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 1 }); // Start on Monday
        const end = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 1 });
        return eachDayOfInterval({ start, end });
    }, [currentMonth]);

    // Create a map for quick access to counts
    const statsMap = useMemo(() => {
        const map = new Map<string, number>();
        statsData?.data.forEach((stat) => {
            map.set(stat.date, stat.count);
        });
        return map;
    }, [statsData]);

    const getIntensityClass = (count: number) => {
        if (count === 0) return 'bg-zinc-100 dark:bg-zinc-800/50';
        if (count <= 1) return 'bg-emerald-200 dark:bg-emerald-900/40 text-emerald-900 dark:text-emerald-100';
        if (count <= 3) return 'bg-emerald-300 dark:bg-emerald-800/60 text-emerald-950 dark:text-emerald-50';
        if (count <= 5) return 'bg-emerald-400 dark:bg-emerald-700/80 text-white';
        return 'bg-emerald-500 dark:bg-emerald-600 text-white';
    };

    const handlePrevMonth = () => setCurrentMonth(prev => subMonths(prev, 1));
    const handleNextMonth = () => setCurrentMonth(prev => addMonths(prev, 1));

    if (isLoading) return <div className={cn("h-48 w-full animate-pulse bg-zinc-100 dark:bg-zinc-900 rounded-lg", className)}></div>;

    return (
        <div className={cn("w-fit mx-auto p-3 bg-white dark:bg-zinc-900 rounded-lg shadow-sm border border-zinc-200 dark:border-zinc-800", className)}>
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-base text-zinc-900 dark:text-zinc-100">
                    {format(currentMonth, 'MMMM yyyy')}
                </h3>
                <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={handlePrevMonth} className="h-7 w-7 hover:bg-zinc-100 dark:hover:bg-zinc-800">
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={handleNextMonth} className="h-7 w-7 hover:bg-zinc-100 dark:hover:bg-zinc-800">
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {/* Weekday Headers */}
            <div className="grid grid-cols-7 gap-1 mb-1">
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map(day => (
                    <div key={day} className="w-7 h-7 flex items-center justify-center text-xs text-zinc-400 font-medium">
                        {day}
                    </div>
                ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
                {days.map((day) => {
                    const dateStr = format(day, 'yyyy-MM-dd');
                    const count = statsMap.get(dateStr) || 0;
                    const isSelected = selectedDate === dateStr;
                    const isCurrentMonth = isSameMonth(day, currentMonth);
                    const isToday = isSameDay(day, new Date());

                    return (
                        <button
                            key={dateStr}
                            onClick={() => onDateSelect?.(isSelected ? undefined : dateStr)}
                            className={cn(
                                "w-7 h-7 rounded-md flex items-center justify-center text-xs transition-all relative",
                                !isCurrentMonth && "opacity-20 grayscale",
                                isSelected && "ring-2 ring-emerald-500 ring-offset-1 dark:ring-offset-zinc-900 z-10 font-bold",
                                getIntensityClass(count),
                                isToday && !isSelected && "ring-1 ring-zinc-300 dark:ring-zinc-600"
                            )}
                            title={`${count} memos on ${format(day, 'MMM d, yyyy')}`}
                        >
                            {format(day, 'd')}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
